import React, { ChangeEvent, useRef } from 'react';
import { connect, getIn, FormikContextType, Field } from 'formik';
import { selectClasses } from '@mui/material';

import { useTranslation } from 'react-i18next';

import { Typography, Checkbox, FormControlLabel, MenuItem, Select } from '~/UIKit';

import { makeStyles } from '~/UIKit/Style';
import { DARKER_GREY, VERTICAL_SEPARATOR, WHITE } from '~/Constants/Colors.constants';
import { FONT_SIZE_LARGE } from '~/Constants/UI.constants';

import { ItemCreationToolTip } from '~/Components';
import { ExtendedLocation } from '../../ExtendedLocation.model';

const useStyles = makeStyles((theme) => ({
  wrapper: {
    paddingBottom: theme.spacing(6),
    display: 'flex',
    flexDirection: 'column',

    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(2),
      background: WHITE,
      flexDirection: 'column',
      alignItems: 'start',
      position: 'relative',
      borderBottom: `thin solid ${VERTICAL_SEPARATOR}`,
    },
  },
  descriptionTitle: {
    fontWeight: 'bolder',
    fontSize: FONT_SIZE_LARGE,
    display: 'flex',
    alignItems: 'center',
    width: 'fit-content',
  },
  description: {
    marginTop: theme.spacing(1),
    width: '100%',
    display: 'grid',
    gridTemplateColumns: '40% 60%',
    gridTemplateRows: '1fr 2fr',
    gridTemplateAreas: `"info info" "format quantity"`,

    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: '60% 40%',
    },
  },
  info: {
    display: 'flex',
    flexGrow: 1,
    gridArea: 'info',

    [theme.breakpoints.down('md')]: {
      marginBottom: theme.spacing(3),
    },
  },
  checkbox: {
    marginRight: 0,
  },
  format: {
    display: 'flex',
    flexDirection: 'column',
    gridArea: 'format',

    div: {
      display: 'flex',
      alignItems: 'center',
    },
  },
  quantity: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-around',
    gridArea: 'quantity',

    '& > div': {
      display: 'flex',
    },

    [theme.breakpoints.down('md')]: {
      alignItems: 'end',
    },
  },
  input: {
    marginLeft: 20,
    height: 28,
    minWidth: 48,
    width: 48,

    [`& .${selectClasses['select']}`]: {
      color: 'currentColor',
      border: `thin solid ${DARKER_GREY}`,
      borderRadius: 5,
      padding: '4px 24px 4px 5px',
    },
  },
}));

export const LocationEditCustomQRCodeFlyer = connect<{}, ExtendedLocation>(
  ({ formik }: { formik: FormikContextType<ExtendedLocation> }) => {
    const TOOLTIP_SIZE = 180;
    const { t } = useTranslation();
    const { classes } = useStyles();
    const containerRef = useRef(null);

    const a5AmountInitialvalue = formik.initialValues.welcomePackageSettings?.a5FlyerAmount;
    const a6AmountInitialvalue = formik.initialValues.welcomePackageSettings?.a6FlyerAmount;

    const a5FlyerFormat: boolean = getIn(formik.values, 'a5FlyerFormat', !!a5AmountInitialvalue);
    const a6FlyerFormat: boolean = getIn(formik.values, 'a6FlyerFormat', !!a6AmountInitialvalue);
    const a5Amountvalue: number = getIn(formik.values, 'welcomePackageSettings.a5FlyerAmount');
    const a6Amountvalue: number = getIn(formik.values, 'welcomePackageSettings.a6FlyerAmount');

    const changeFlyerFormatHandler = (flyerType: string, e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.checked;

      formik.setFieldValue(`${flyerType}Format`, value);

      if (!value) {
        formik.setFieldValue(`welcomePackageSettings.${flyerType}Amount`, 0);
      }
    };

    const selectOptions = (total = 11) =>
      [...Array(total)].map((_, index) => (
        <MenuItem key={index} value={index}>
          {index}
        </MenuItem>
      ));

    return (
      <div className={classes.wrapper} ref={containerRef}>
        <Typography className={classes.descriptionTitle}>
          {t('settings.title.qrCode.flyerFormat')}
        </Typography>
        <div className={classes.description}>
          <div className={classes.info}>{t('settings.title.qrCode.flyerFormat.description')}</div>
          <div className={classes.format}>
            <div>
              <FormControlLabel
                className={classes.checkbox}
                control={
                  <Checkbox
                    onChange={(e) => changeFlyerFormatHandler('a6Flyer', e)}
                    checked={a6FlyerFormat}
                  />
                }
                label={t('settings.title.qrCode.flyerFormat.a6')}
              />
              <ItemCreationToolTip
                title={t('settings.title.qrCode.flyerFormat.a6.cm')}
                containerRef={containerRef}
                width={TOOLTIP_SIZE}
              />
            </div>
            <div>
              <FormControlLabel
                className={classes.checkbox}
                control={
                  <Checkbox
                    checked={a5FlyerFormat}
                    onChange={(e) => changeFlyerFormatHandler('a5Flyer', e)}
                  />
                }
                label={t('settings.title.qrCode.flyerFormat.a5')}
              />
              <ItemCreationToolTip
                title={t('settings.title.qrCode.flyerFormat.a5.cm')}
                containerRef={containerRef}
                width={TOOLTIP_SIZE}
              />
            </div>
          </div>
          <div className={classes.quantity}>
            <div>
              <Typography> {t('settings.title.qrCode.flyerFormat.selectQuantity')}</Typography>

              <Field
                className={classes.input}
                component={Select}
                fullWidth
                name="welcomePackageSettings.a6FlyerAmount"
                type="select"
                newDesign
                disabled={!a6FlyerFormat}
                error={a6Amountvalue <= 0 && a6FlyerFormat}
                inputProps={{ maxLength: 50 }}
              >
                {selectOptions()}
              </Field>
            </div>
            <div>
              <Typography>{t('settings.title.qrCode.flyerFormat.selectQuantity')}</Typography>
              <Field
                className={classes.input}
                component={Select}
                fullWidth
                name="welcomePackageSettings.a5FlyerAmount"
                type="select"
                newDesign
                disabled={!a5FlyerFormat}
                error={a5Amountvalue <= 0 && a5FlyerFormat}
                inputProps={{ maxLength: 50 }}
              >
                {selectOptions()}
              </Field>
            </div>
          </div>
        </div>
      </div>
    );
  },
);
